import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import logoDark from "@/assets/logo-dark.png";
import logoLight from "@/assets/logo-light.png";

export const Navbar = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > 20
  );
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !open;

  const links = [
    { to: "/apartments", label: t("nav.apartments") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/guide", label: t("nav.guide") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent border-b border-transparent py-2"
          : "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#0F292F]/10 py-2"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
          <img
            src={transparent ? logoLight : logoDark}
            alt="Fiorucci Apartments — Case Vacanze"
            className={`w-auto transition-all duration-500 ${transparent ? "h-[96px] md:h-[120px]" : "h-[90px] md:h-[104px]"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide transition-colors ${
                  transparent ? "text-white/90 hover:text-white" : "text-petrol/80 hover:text-petrol"
                } ${isActive ? "font-medium" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <LanguageSwitcher transparent={transparent} />
          <Link
            to="/apartments"
            data-testid="nav-cta-checkin"
            className={`hidden md:inline-flex items-center text-sm px-5 py-2.5 rounded-sm transition-all duration-300 ${
              transparent
                ? "bg-white/95 text-petrol hover:bg-white"
                : "bg-petrol text-white hover:bg-[#1A3A41]"
            }`}
          >
            {t("hero.cta_discover")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden ${transparent ? "text-white" : "text-petrol"}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FDFBF7] border-t border-line mt-3 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.to.replace("/", "")}`} className="text-petrol text-lg font-serif">
              {l.label}
            </Link>
          ))}
          <Link to="/admin" className="text-muted-foreground text-sm mt-2">
            {t("nav.admin")}
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
